import {
  Lock, Camera, Save, Send, CheckCircle2, XCircle, RotateCcw, Loader2,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "../../components/ui/button";

// =====================================================================
// ICSubmitBar — Submit / lock panel below the form.
// Three branches:
//   • locked → grey notice + "Start a new IC"
//   • approver → Draft + Send to IC + Approve + Under Discussion + Reject
//   • non-approver → Draft + Send to IC only
// =====================================================================
export default function ICSubmitBar({
  locked, editing, canApprove, submitting, shooting,
  onSubmit, onScreenshot, onResetForm,
}) {
  return (
    <>
      {locked ? (
        <div className="bg-zinc-100 border border-zinc-300 rounded-xl p-4 flex items-center justify-between gap-3 text-zinc-700" data-testid="ic-submit-locked">
          <div className="flex items-center gap-2 text-[12px]">
            <Lock className="w-4 h-4" />
            This IC is approved and locked. Numbers above are read-only.
          </div>
          <button onClick={onResetForm} className="text-[11.5px] font-medium px-3 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-1.5" data-testid="ic-submit-locked-new">
            <RotateCcw className="w-3.5 h-3.5" /> Start a new IC
          </button>
        </div>
      ) : (
        <div className="bg-zinc-900 text-white rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 shadow-md">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11.5px] text-zinc-300">
              {editing?.editing_approved
                ? "Editing Approved IC (admin override) — saving will overwrite the record"
                : editing ? `Updating existing IC (${editing.decision})` : (canApprove ? "Save as draft, send to IC, or approve directly" : "Save as draft or submit to Financial Review")}
            </span>
            <button
              onClick={onScreenshot}
              disabled={shooting}
              data-testid="ic-submit-screenshot"
              className="text-[11px] px-2 py-1 rounded border border-zinc-700 bg-zinc-800 text-zinc-100 hover:bg-zinc-700 disabled:opacity-60 inline-flex items-center gap-1 font-medium"
              title="Save IC as PNG for WhatsApp"
            >
              {shooting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Screenshot
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onSubmit("Draft")} disabled={submitting} data-testid="ic-submit-draft" className="h-9 rounded-md bg-zinc-700 text-white hover:bg-zinc-600 text-[12px] gap-1.5 font-bold">
              <Save className="w-3.5 h-3.5" /> Save
            </Button>
            <Button onClick={() => onSubmit("Financial Review")} disabled={submitting} data-testid="ic-submit-fr" className="h-9 rounded-md bg-brand-yellow text-zinc-900 hover:bg-brand-yellow/90 text-[12px] gap-1.5 font-bold">
              <Send className="w-3.5 h-3.5" /> Send to IC
            </Button>
            {canApprove && (
              <>
                <Button onClick={() => onSubmit("Approved")} disabled={submitting} data-testid="ic-submit-approve" className="h-9 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-[12px] gap-1.5 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve {editing ? "(locks)" : ""}
                </Button>
                <Button onClick={() => onSubmit("Under Discussion")} disabled={submitting} data-testid="ic-submit-discuss" className="h-9 rounded-md bg-amber-500 text-white hover:bg-amber-600 text-[12px] font-bold">
                  Under Discussion
                </Button>
                <Button onClick={() => onSubmit("Rejected")} disabled={submitting} data-testid="ic-submit-reject" className="h-9 rounded-md bg-rose-600 text-white hover:bg-rose-700 text-[12px] gap-1.5 font-bold">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
        <FileSpreadsheet className="w-3 h-3" /> An IC report (xlsx) auto-downloads on save. Re-download anytime from "Past IC submissions" or the banner above.
      </div>
    </>
  );
}
